import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CategoryTree,
  CategoryTreeQuery,
  useCategoryTreeLazyQuery,
} from '@/lib/graphql/generated/graphql';
import { mapI18nLanguageToLanguageIso6391 } from '@/lib/utils';
import {
  categoryTreeLoadedForLanguageAtom,
  categoryTreeMapState,
  categoryTreeState,
} from '@/states/category-tree-map.state';
import { useAtomValue, useSetAtom, useStore } from 'jotai';

/**
 * Hook to get category tree and create a map from category ID to label
 * This is used for filtering and displaying category labels
 */
export const useCategoryTreeMap = (): {
  categoryTree: CategoryTree[];
  categoryTreeMap: Record<string, string>;
} => {
  const { i18n } = useTranslation();
  const setCategoryTreeMap = useSetAtom(categoryTreeMapState);
  const setCategoryTree = useSetAtom(categoryTreeState);
  const setLoadedForLanguage = useSetAtom(categoryTreeLoadedForLanguageAtom);
  const language = mapI18nLanguageToLanguageIso6391(i18n.language);

  const jotaiStore = useStore();

  const [executeFetch] = useCategoryTreeLazyQuery({
    fetchPolicy: 'no-cache',
    context: { preferGet: true },
  });

  useEffect(() => {
    const loadedForLang = jotaiStore.get(categoryTreeLoadedForLanguageAtom);
    const hasData = jotaiStore.get(categoryTreeState).length > 0;
    if (hasData && loadedForLang === language) return; // skip on navigation remount

    let cancelled = false;
    void executeFetch({ variables: { language } })
      .then((result) => {
        if (cancelled || !result.data?.categoryTree) return;
        const tree = result.data.categoryTree;

        const map: Record<string, string> = {};
        const flatten = (nodes: CategoryTreeQuery['categoryTree']): void => {
          nodes.forEach((node) => {
            map[node.id] = node.label;
            if (node.subCategories && node.subCategories.length > 0) {
              flatten(node.subCategories);
            }
          });
        };
        flatten(tree);

        setCategoryTree(tree);
        setCategoryTreeMap(map);
        setLoadedForLanguage(language);
      })
      .catch(console.error);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const categoryTree = useAtomValue(categoryTreeState);
  const categoryTreeMap = useAtomValue(categoryTreeMapState);

  return { categoryTree, categoryTreeMap };
};
